// Math & Bit Manipulation — full course content for the DSA Explanation Modal.
// Java-focused. Same section-course schema as intervals.js: concept → internals →
// applied templates → errors → complexity → beginner drills → cheatsheet → playground.

import mathBitDrills from './mathBitDrills';
const drill = id => mathBitDrills.find(d => d.id === id);

const mathBit = {
  key: 'Math & Bit Manipulation',
  tagline: 'Closed-form formulas and the raw bits underneath every number — the toolkit that skips loops entirely.',

  sections: [
    {
      id: 'intro',
      title: 'Introduction to Math & Bit Manipulation',
      type: 'text',
      body: [
        "This pattern is a toolkit rather than a single technique: closed-form math formulas that replace a loop with a direct calculation, and bit-level operations that manipulate a number's binary representation directly, one bit (or one operator) at a time.",
        "What ties them together is the same underlying habit: before reaching for a hash map, a sort, or a full scan, ask whether the numbers involved have a mathematical structure — a sum formula, a cancellation property, a binary pattern — that skips the work entirely.",
      ],
    },
    {
      id: 'why',
      title: 'Why Math & Bit Manipulation?',
      type: 'text',
      body: [
        "Many problems that LOOK like they need extra memory (a hash set to track duplicates, an array to count occurrences) actually have a mathematical shortcut that needs none at all. XOR cancels exact duplicates. A sum formula computes \"what SHOULD be here\" without ever storing what actually is. These tricks turn O(n)-space solutions into O(1)-space ones, which is exactly the kind of follow-up interviewers love to ask for.",
        "Bit manipulation specifically matters because computers already store every number in binary — operations like shifting, masking, and clearing the lowest set bit are things the hardware does in a single cycle, dramatically faster than the equivalent loop-based arithmetic. Recognizing when a problem is secretly ABOUT the binary representation (powers of two, counting set bits, bitwise addition) is the core skill this pattern trains.",
      ],
    },
    {
      id: 'analogy',
      title: 'How Math & Bit Manipulation Works — Two Real-World Pictures',
      type: 'text',
      heading: 'A Sock Drawer, and a Row of Light Switches',
      body: [
        "Picture folding laundry: every matching PAIR of socks gets folded together and put away, cancelling out of the pile entirely. Whatever's left on the floor at the end — the one unmatched sock — is instantly visible, without ever needing to count how many socks of each color there were. That's exactly what XOR does with numbers: x ^ x = 0 makes every duplicate pair vanish, leaving only the single unpaired value exposed.",
        "Now picture a wall of light switches, each one controlling a different, FIXED power of two — switch 0 controls 1, switch 1 controls 2, switch 2 controls 4, and so on. Any number is just some particular combination of switches flipped on. Once a number is seen this way, bit tricks stop being magic: checking a switch is n & (1 << i), flipping one is n ^ (1 << i), and turning off just the rightmost switch that's currently on is n & (n - 1) — every trick in this pattern is a small, mechanical action on that row of switches.",
      ],
    },
    {
      id: 'visualize',
      title: 'Visualizing a Number\'s Bits',
      type: 'bit-diagram',
      body: [
        "This is 11 in binary: 1011. Reading right to left, the set bits (1s) are at positions 0, 1, and 3 — meaning 11 = 8 + 2 + 1.",
        "Brian Kernighan's trick, n & (n-1), always clears exactly the RIGHTMOST 1-bit shown here — turning 1011 into 1010 in one operation, without touching any bit above it.",
      ],
      demo: {
        bits: ['1', '0', '1', '1'],
        label: 'n = 11',
        highlightIndices: [3],
        note: 'The highlighted rightmost 1-bit is exactly the one n & (n-1) would clear.',
      },
    },
    {
      id: 'template',
      title: 'The Core Bit-Trick Vocabulary',
      type: 'code',
      code:
`// Every bit-manipulation problem in this pattern combines a small
// number of these building blocks:
int isBitSet            = (n >> i) & 1;        // is bit i set?
int setBit               = n | (1 << i);        // turn bit i on
int clearBit             = n & ~(1 << i);       // turn bit i off
int toggleBit            = n ^ (1 << i);        // flip bit i
int clearLowestSetBit    = n & (n - 1);         // turn OFF the lowest set bit
int isolateLowestSetBit  = n & (-n);            // isolate JUST the lowest set bit
boolean isPowerOfTwo     = n > 0 && (n & (n - 1)) == 0;`,
      note: 'Memorize this vocabulary once — nearly every bit-manipulation problem is some direct application (or small variation) of one of these seven lines.',
    },
    {
      id: 'xortemplate',
      title: 'Applied: The XOR-Cancellation Pattern',
      type: 'code',
      code:
`int xorCancel(int[] nums) {
    int result = 0;
    for (int n : nums) {
        result ^= n;   // x ^ x = 0, x ^ 0 = x — duplicates cancel, survivors remain
    }
    return result;
}`,
      note: 'Whenever a problem involves values that should pair up and cancel — Single Number, and (with a twist folding in the index range) Missing Number\'s XOR approach — this exact accumulation loop is the answer.',
    },
    {
      id: 'mathtricktemplate',
      title: 'Applied: Closed-Form Sums and Safe Digit Extraction',
      type: 'code',
      code:
`// Closed-form sums avoid a loop entirely:
int sumOfZeroToN = n * (n + 1) / 2;

// Digit-by-digit extraction, without ever converting to a string:
int digit = n % 10;
n /= 10;

// Checking for overflow BEFORE it happens, not after:
if (result > Integer.MAX_VALUE / 10) {
    /* the next multiplication would overflow — bail out now */
}`,
      note: 'The sum formula n(n+1)/2 powers Missing Number\'s math approach. Digit extraction via % and / (never string conversion) powers Reverse Integer and Happy Number — and checking for overflow BEFORE performing an operation, rather than inspecting the result after, is what keeps these safe.',
    },
    {
      id: 'errors',
      title: 'Common Errors',
      type: 'errors',
      items: [
        {
          mistake: 'Reaching for a HashSet/HashMap when the constraints explicitly forbid extra space',
          why: 'Problems like Single Number and Missing Number often explicitly ask for O(1) space. A hash-based counting solution is easy to write first, but doesn\'t satisfy that constraint, and interviewers will follow up asking for the O(1) version specifically.',
          fix: 'Notice explicit space constraints in the prompt early, and specifically look for a cancellation trick (XOR) or closed-form formula (sum) that avoids auxiliary storage altogether.',
        },
        {
          mistake: 'Using >> instead of >>> (or vice versa) on negative or unsigned-intended numbers',
          why: 'Java\'s >> is an ARITHMETIC right shift that preserves the sign bit (fills with 1s for negative numbers); >>> is a LOGICAL right shift that always fills with 0s. Using the wrong one on a negative number, or a number meant to be treated as unsigned, silently produces a completely different bit pattern.',
          fix: 'For genuinely unsigned bit manipulation (like Number of 1 Bits, which treats its input as an unsigned 32-bit value), always use >>> — reserve >> for cases where sign-preservation is actually intended.',
        },
        {
          mistake: 'Letting an overflow happen before checking for it',
          why: 'In Reverse Integer, computing result * 10 + digit and THEN checking whether it overflowed is unreliable — Java\'s integer overflow silently wraps the value around, so a bogus wrapped value can already exist before any check runs.',
          fix: 'Always check whether an operation WOULD overflow using safe pre-computed thresholds (like Integer.MAX_VALUE / 10) before performing it, never after.',
        },
        {
          mistake: 'Assuming a sum-formula or XOR trick generalizes to more than one "anomaly"',
          why: 'Single Number\'s XOR trick and Missing Number\'s sum-formula both rely on EXACTLY one unpaired or missing value. With two missing numbers, or two singly-occurring numbers, both tricks silently produce a garbled, combined result instead of an error.',
          fix: 'Double-check the problem\'s exact guarantee (exactly one missing, exactly one single) before reusing these tricks — a different problem shape (like Single Number II or III) needs a genuinely different technique.',
        },
      ],
    },
    {
      id: 'complexity',
      title: 'Time Complexity',
      type: 'complexity',
      rows: [
        { op: 'XOR cancellation (Single Number, Missing Number)', time: 'O(n)', note: 'One pass, O(1) space — no hashing needed.' },
        { op: 'Sum formula (Missing Number, math approach)', time: 'O(n)', note: 'One pass to sum the array, plus a constant-time formula.' },
        { op: 'Bit counting (Number of 1 Bits, Kernighan\'s trick)', time: 'O(k), k = number of set bits', note: 'Visits only the 1-bits, never the 0-bits in between.' },
        { op: 'DP over bit relationships (Counting Bits)', time: 'O(n)', note: 'Each answer reuses a strictly smaller, already-computed index.' },
        { op: 'Binary exponentiation (Pow(x, n))', time: 'O(log n)', note: 'Halves the exponent every step instead of decrementing by 1.' },
      ],
    },
    {
      id: 'when',
      title: 'When to Use',
      type: 'text',
      heading: 'Reach for Math & Bit Manipulation when you see…',
      body: [
        'A problem involving pairs, duplicates, or "everything appears twice except one" — a strong signal for XOR cancellation.',
        'A tight O(1)-space constraint alongside an otherwise straightforward counting problem — look for a closed-form math formula or an XOR trick before reaching for a hash structure.',
        'The problem talks explicitly about bits, binary representation, or powers of two — the core bit-trick vocabulary (mask, clear-lowest-bit, shift) almost always applies directly.',
        'A sequence defined by repeatedly applying some deterministic function to a number, asking whether it reaches a fixed target — a strong signal for cycle detection (HashSet, or Floyd\'s for O(1) space).',
        'Repeated multiplication or exponentiation with a fixed base — binary exponentiation (halve the exponent, square the base) turns O(n) into O(log n).',
      ],
    },
    {
      id: 'problems',
      title: 'Beginner Problems (Easy → Medium → Advanced)',
      type: 'problems',
      groups: [
        {
          level: 'Easy',
          items: [
            drill('single-number-drill'),
            drill('reverse-bits'),
            drill('power-of-two'),
          ],
        },
        {
          level: 'Medium',
          items: [
            drill('number-of-1-bits-drill'),
            drill('sum-of-two-integers'),
            drill('counting-bits-drill'),
          ],
        },
        {
          level: 'Advanced',
          items: [
            drill('reverse-integer'),
            drill('gcd-of-two-numbers'),
            drill('pow-x-n'),
          ],
        },
      ],
      note: 'These are foundation drills to build fluency with the core bit-trick vocabulary and common math shortcuts before moving into the curated interview problem list for this pattern.',
    },
    {
      id: 'cheatsheet',
      title: 'Cheatsheet & Recap',
      type: 'cheatsheet',
      items: [
        'XOR cancels exact duplicates: x ^ x = 0, x ^ 0 = x. Use it whenever a problem has values that should pair off, with exactly one exception.',
        'n & (n-1) clears the lowest set bit — the basis of Brian Kernighan\'s bit-counting trick, and of the n > 0 && (n & (n-1)) == 0 power-of-two check.',
        'A closed-form sum formula (n(n+1)/2) or a DP relationship (dp[i >> 1]) often replaces a loop that would otherwise recompute related answers from scratch.',
        'Check for overflow BEFORE performing an operation (using thresholds like Integer.MAX_VALUE / 10), never by inspecting the result after — overflow already silently wraps by then.',
        'Java\'s >> preserves the sign bit (arithmetic shift); >>> always fills with 0s (logical shift) — use >>> for unsigned-intended bit manipulation.',
        'Binary exponentiation (square the base, halve the exponent) turns O(n) repeated multiplication into O(log n).',
        'A cycle-detection problem (Happy Number) can use a HashSet for clarity, or Floyd\'s slow/fast pointers for O(1) space — the same technique used for linked list cycles.',
      ],
    },
    {
      id: 'playground',
      title: 'Interactive Practice & Quiz',
      type: 'playground',
    },
  ],
};

export default mathBit;
